import { snakeCase } from 'lodash';

import { mapRecordFromServer, mapRecordsFromServer } from 'core/mappers';
import { partfinitiClient } from 'core/services';
import { GetManyResponse, GetOneResponse } from 'core/types';
import { getQueryStringToSnakeCase, objectKeysSnakeCaseToCamelCase } from 'core/utils';
import {
  ICorporatePart,
  ICorporatePartCostListItem,
  ICorporatePartCostListPayload,
  ICorporatePartExtraData,
  ICorporatePartPriceListItem,
  ICorporatePartPriceListPayload,
  ICorporatePartRequestPayload,
  UpdateCorporatePartForm,
} from 'corporate/types';

export const getCorporatePart = async (
  requestPayload: ICorporatePartRequestPayload,
  signal: AbortSignal
): Promise<GetOneResponse<ICorporatePart, ICorporatePartExtraData>> => {
  const { data } = await partfinitiClient.get(
    `/corporate_v2/part/get-details?${getQueryStringToSnakeCase(requestPayload, {
      removeNullValues: true,
    })}`,
    signal
  );

  const result = mapRecordFromServer<ICorporatePart>(data.pnb_configuration || data.corporate_part);

  return {
    ...result,
    record: {
      ...(result.record && mapExtraFields(result.record)),
      inventory: objectKeysSnakeCaseToCamelCase(data.inventory),
      isPnb: !!data.pnb_configuration,
    },
    extraData: {
      externalPrices: objectKeysSnakeCaseToCamelCase(data.external_prices),
    },
  };
};

export const upsertCorporatePart = async (data: { corporatePart: UpdateCorporatePartForm }) =>
  partfinitiClient.postForm('/corporate/parts/save', data, {
    toSnakeCase: snakeCase,
    includeNullValues: true,
  });

export const updateCorporatePartPricingType = async (data: {
  corporatePart: {
    id: number;
    pricingType: string;
    listPrice?: number | null;
    listPriceUom?: string;
    floorPrice?: number | null;
    floorPriceUom?: string;
  };
}) =>
  partfinitiClient.postForm('/corporate/parts/save', data, {
    toSnakeCase: snakeCase,
    includeNullValues: true,
  });

export const getCorporatePartCosts = async (
  requestPayload: ICorporatePartCostListPayload,
  signal?: AbortSignal
): Promise<GetManyResponse<ICorporatePartCostListItem>> => {
  const { data } = await partfinitiClient.get(
    `/corporate/parts/part-costs?${getQueryStringToSnakeCase(requestPayload)}`,
    signal
  );

  return mapRecordsFromServer<ICorporatePartCostListItem>(data?.data, data?.recordsFiltered);
};

export const getCorporatePartPrices = async (
  requestPayload: ICorporatePartPriceListPayload,
  signal?: AbortSignal
): Promise<GetManyResponse<ICorporatePartPriceListItem>> => {
  const { data } = await partfinitiClient.get(
    `/corporate/parts/part-prices?${getQueryStringToSnakeCase(requestPayload)}`,
    signal
  );

  return mapRecordsFromServer<ICorporatePartPriceListItem>(data?.data, data?.recordsFiltered);
};

const mapExtraFields = (record: ICorporatePart): ICorporatePart => {
  const uomString = record.uom || '';

  return {
    ...record,
    minimumQuantityWithUom: record.minimumQuantity && `${record.minimumQuantity} ${uomString}`,
    orderIncrementWithUom: record.orderIncrement && `${record.orderIncrement} ${uomString}`,
    manufacturerMinimumQuantityWithUom:
      record.manufacturerMinimumQuantity && `${record.manufacturerMinimumQuantity} ${uomString}`,
    manufacturerOrderIncrementWithUom:
      record.manufacturerOrderIncrement && `${record.manufacturerOrderIncrement} ${uomString}`,
    pkgUnitsWithUom: record.pkgUnits && `${record.pkgUnits} ${uomString}`,
  };
};
